import React, { useState, useCallback } from 'react';
import { useClientPerformance } from '../../hooks/useClientPerformance';
import { ClientPerformanceFilters } from '../../types';
import PerformanceTable from './PerformanceTable';
import PerformanceStats from './PerformanceStats';
import ReportFilters from './ReportFilters';
import MotivationalLoader from '../MotivationalLoader';
import './ClientPerformanceReport.css';

const ClientPerformanceReport: React.FC = () => {
  // Get yesterday's date
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  
  const [filters, setFilters] = useState<ClientPerformanceFilters>({
    dateFrom: yesterdayStr, // Yesterday
    dateTo: yesterdayStr // Yesterday
  });

  const {
    data,
    loading,
    error,
    refresh,
    exportData,
    stats
  } = useClientPerformance(filters);

  const handleFilterChange = useCallback((newFilters: ClientPerformanceFilters) => {
    setFilters(newFilters);
  }, []);

  const handleRefresh = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const handleExport = useCallback(() => {
    exportData();
  }, [exportData]);

  if (loading) {
    return (
      <div className="client-performance-report">
        <div className="performance-loading">
          <MotivationalLoader type="dashboard" size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-performance-report">
        <div className="performance-error">
          <div className="error-icon">⚠️</div>
          <h2>Unable to Load Performance Report</h2>
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="client-performance-report">
      <div className="performance-header">
        <div className="header-content">
          <h1 className="page-title">Client Performance Report</h1>
          <p className="page-subtitle">
            Comprehensive email delivery metrics and domain reputation analysis
          </p>
        </div>
        <div className="header-actions">
          <button onClick={handleRefresh} className="action-button refresh-button">
            <span className="button-icon">🔄</span>
            Refresh
          </button>
          <button onClick={handleExport} className="action-button export-button">
            <span className="button-icon">📥</span>
            Export CSV
          </button>
        </div>
      </div>

      <PerformanceStats stats={stats} />

      <ReportFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <div className="performance-content">
        {data.length === 0 ? (
          <div className="no-data-message">
            <div className="no-data-icon">📊</div>
            <h3>No Performance Data Available</h3>
            <p>Try adjusting your filters or date range to see results.</p>
          </div>
        ) : (
          <PerformanceTable data={data} />
        )}
      </div>
    </div>
  );
};

export default ClientPerformanceReport;